/**
 * L'administration du locataire — les membres, leurs rôles, les réglages.
 *
 * Ce que l'espace de travail gère de lui-même : qui en fait partie et avec quel
 * rôle, le nom de l'espace, son mode d'accueil, la conservation. Réservé aux
 * administrateurs — le rail ne montre l'entrée qu'à eux, et le portail refuse
 * l'accès à tout autre : on le dit alors franchement plutôt que de rendre une
 * page vide.
 *
 * **On ne se change ni le rôle ni l'état soi-même** : ces gestes-là sont
 * désactivés pour sa propre ligne, et le portail les refuserait de toute façon
 * (ne pas fermer la porte de l'intérieur).
 */
import { useState } from "react";

import { api, ErreurApi } from "../api/client";
import { Chargement, Echec } from "../composants/etats";
import { quand } from "../composants/formats";
import { useDonnees } from "../composants/donnees";

interface Membre {
  user_id: string; nom: string | null; courriel: string | null;
  role: string; etat: string; derniere_activite: string | null;
}
interface Parametres {
  label: string; etat: string; accueil: string;
  conservation: { sources: number | null; journal: number | null };
  modes_accueil: string[]; durees_conservation: number[];
}

const ROLES: Record<string, string> = {
  MEMBER: "Membre", TENANT_ADMIN: "Administrateur",
  PLATFORM_ADMIN: "Admin plateforme",
};
const ACCUEIL: Record<string, string> = {
  CLOSED: "Fermé (sur invitation)", JIT: "À la première connexion",
  APPROVAL: "Sur approbation",
};

export function Administration({ moiId, role }: { moiId?: string; role?: string }) {
  const admin = role === "TENANT_ADMIN" || role === "PLATFORM_ADMIN";
  if (!admin) return <Reserve />;
  return (
    <div className="ecran">
      <div className="ecran__tete">
        <div className="rangee rangee--bas">
          <div>
            <h1 className="ecran__titre">Administration</h1>
            <div className="ecran__compte">Les membres et les réglages de l'espace</div>
          </div>
        </div>
      </div>
      <div className="ecran__corps pile">
        <Reglages />
        <Membres moiId={moiId} />
      </div>
    </div>
  );
}

function Reserve() {
  return (
    <div className="ecran"><div className="ecran__corps">
      <div className="carte"><div className="carte__corps pile" style={{ textAlign: "center" }}>
        <h2>Réservé aux administrateurs</h2>
        <span className="discret">
          Cette page gère les membres et les réglages de l'espace. Demandez à un
          administrateur du locataire d'y accéder.
        </span>
      </div></div>
    </div></div>
  );
}

function Reglages() {
  const p = useDonnees<{ donnees: Parametres }>(
    () => api.lire("/api/v1/administration/parametres"), []);
  const [erreur, poserErreur] = useState("");

  async function poser(chemin: string, corps: unknown) {
    poserErreur("");
    try {
      await api.modifier(`/api/v1/administration/${chemin}`, corps);
      p.recharger();
    } catch (exc) {
      poserErreur(exc instanceof ErreurApi ? exc.message : "Enregistrement refusé.");
    }
  }

  if (p.charge && !p.donnees) return <div className="carte"><Chargement lignes={2} /></div>;
  if (p.erreur) return <div className="carte"><div className="carte__corps">
    <Echec erreur={p.erreur} reessayer={p.recharger} /></div></div>;
  const d = p.donnees?.donnees;
  if (!d) return null;

  return (
    <div className="carte"><div className="carte__corps pile pile--serree">
      <h3 style={{ margin: 0 }}>Réglages de l'espace</h3>
      {erreur && <span className="discret" role="alert">{erreur}</span>}
      <div className="adm-reglages">
        <label className="adm-champ">
          <span className="discret">Nom de l'espace</span>
          <input defaultValue={d.label} key={d.label}
                 onBlur={(e) => {
                   const v = e.target.value.trim();
                   if (v && v !== d.label) void poser("parametres", { label: v });
                 }}
                 onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} />
        </label>
        <label className="adm-champ">
          <span className="discret">Accueil des nouveaux membres</span>
          <select value={d.accueil}
                  onChange={(e) => void poser("parametres", { accueil: e.target.value })}>
            {d.modes_accueil.map((m) => (
              <option key={m} value={m}>{ACCUEIL[m] ?? m}</option>
            ))}
          </select>
        </label>
        <label className="adm-champ">
          <span className="discret">Conservation des sources</span>
          <select value={String(d.conservation.sources ?? 0)}
                  onChange={(e) => void poser("conservation", { sources: Number(e.target.value) })}>
            {d.durees_conservation.map((n) => (
              <option key={n} value={n}>{n === 0 ? "Illimitée" : `${n} ans`}</option>
            ))}
          </select>
        </label>
        <label className="adm-champ">
          <span className="discret">Conservation du journal</span>
          <select value={String(d.conservation.journal ?? 0)}
                  onChange={(e) => void poser("conservation", { journal: Number(e.target.value) })}>
            {d.durees_conservation.map((n) => (
              <option key={n} value={n}>{n === 0 ? "Illimitée" : `${n} ans`}</option>
            ))}
          </select>
        </label>
      </div>
    </div></div>
  );
}

function Membres({ moiId }: { moiId?: string }) {
  const m = useDonnees<{ donnees: Membre[] }>(
    () => api.lire("/api/v1/administration/membres"), []);
  const [erreur, poserErreur] = useState("");

  async function modifier(user_id: string, corps: { role?: string; etat?: string }) {
    poserErreur("");
    try {
      await api.modifier(`/api/v1/administration/membres/${user_id}`, corps);
      m.recharger();
    } catch (exc) {
      poserErreur(exc instanceof ErreurApi ? exc.message : "Modification refusée.");
      m.recharger();
    }
  }

  const liste = m.donnees?.donnees ?? [];
  return (
    <div className="carte"><div className="carte__corps pile pile--serree">
      <div className="rangee rangee--espacee">
        <h3 style={{ margin: 0 }}>Membres <span className="discret">({liste.length})</span></h3>
      </div>
      {erreur && <span className="discret" role="alert">{erreur}</span>}
      {m.charge && !m.donnees ? (
        <Chargement lignes={3} />
      ) : m.erreur ? (
        <Echec erreur={m.erreur} reessayer={m.recharger} />
      ) : (
        <div className="defilant">
          <table className="tableau adm-table">
            <thead><tr>
              <th>Membre</th><th>Rôle</th><th>État</th><th>Dernière activité</th>
            </tr></thead>
            <tbody>
              {liste.map((u) => {
                const soi = u.user_id === moiId;
                const plateforme = u.role === "PLATFORM_ADMIN";
                return (
                  <tr key={u.user_id} className={u.etat === "DISABLED" ? "adm-desactive" : ""}>
                    <td>
                      <div className="pile pile--serree">
                        <strong>{u.nom || <span className="faible">Sans nom</span>}
                          {soi && <span className="discret"> · vous</span>}</strong>
                        <span className="discret">{u.courriel || "—"}</span>
                      </div>
                    </td>
                    <td>
                      {plateforme ? (
                        <span className="etiquette">{ROLES[u.role]}</span>
                      ) : (
                        <select value={u.role} disabled={soi}
                                title={soi ? "On ne change pas son propre rôle" : undefined}
                                onChange={(e) => void modifier(u.user_id, { role: e.target.value })}>
                          <option value="MEMBER">{ROLES.MEMBER}</option>
                          <option value="TENANT_ADMIN">{ROLES.TENANT_ADMIN}</option>
                        </select>
                      )}
                    </td>
                    <td>
                      <button type="button"
                              className={`bouton bouton--discret adm-etat adm-etat--${u.etat.toLowerCase()}`}
                              disabled={soi}
                              title={soi ? "On ne se désactive pas soi-même" : undefined}
                              onClick={() => void modifier(u.user_id,
                                { etat: u.etat === "ACTIVE" ? "DISABLED" : "ACTIVE" })}>
                        {u.etat === "ACTIVE" ? "Actif" : "Désactivé"}
                      </button>
                    </td>
                    <td className="discret">
                      {u.derniere_activite ? quand(u.derniere_activite)
                        : u.etat === "INVITED" ? "Invité" : "Jamais"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div></div>
  );
}
